import { useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import MinorSector from 'components/MapViewAngle/MinorSector';

// Import PNG icons
// import mapIcon from './map-icon.png';
// import cancelIcon from './cancel.png';
import expandIcon from './quit-pip.png';
import quitFullscreenIcon from './quit-fullscreen.png';
import { CloseIcon, MapIcon } from '../../assets/icons';

import './style.scss';
import { createPortal } from 'react-dom';

const FloorMaps = ({
  floorMaps,
  currentPano,
  onSelect,
  cameraParams,
  sceneRotation,
}) => {
  const [isOpen, setIsOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(() => window.innerWidth <= 450);
  const [hoveredHotspot, setHoveredHotspot] = useState(null);
  const [expandedWidth, setExpandedWidth] = useState(680);
  const [isDesktop, setIsDesktop] = useState(() => window.innerWidth > 450);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth <= 450);
  const { scenes } = useSelector((state) => state);

  const [headingIndicatorShow, setHeadingIndicatorShow] = useState(false);

  useEffect(() => {
    setHeadingIndicatorShow(false);

    setTimeout(() => {
      setHeadingIndicatorShow(true);
    }, 500);
  }, [currentPano]);

  // Get the first floor map (assuming there's only one for now)
  const floorMap = floorMaps && floorMaps.length > 0 ? floorMaps[0] : null;

  // Handle hotspot click to navigate to scene
  const handleHotspotClick = useCallback(
    (hotspot) => {
      const targetScene = scenes.find((scene) => scene._id === hotspot.id);
      if (targetScene && onSelect) {
        onSelect(targetScene.groupId, targetScene._id);
      }
    },
    [scenes, onSelect]
  );

  // Hooks must run before any early return

  const handleToggleMap = () => {
    const nextOpen = !isOpen;
    setIsOpen(nextOpen);
    if (nextOpen) {
      if (isMobile) {
        setIsExpanded(true);
      }
    } else {
      setIsExpanded(false);
    }
  };

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  // Track viewport to restrict logic to desktop only
  useEffect(() => {
    const handleResize = () => {
      const w = window.innerWidth;
      setIsDesktop(w > 450);
      setIsMobile(w <= 450);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Measure image natural size to decide expanded width (desktop only)
  useEffect(() => {
    if (!isDesktop || !floorMap?.image?.url) return;
    const img = new Image();
    img.onload = () => {
      const isPortrait = img.naturalHeight > img.naturalWidth;
      setExpandedWidth(isPortrait ? 460 : 680);
    };
    img.src = floorMap.image.url;
  }, [floorMap?.image?.url, isDesktop]);

  // Don't render if no floor map data
  if (!floorMap || !floorMap.image) {
    return null;
  }

  return createPortal(
    <div
      className={`floor-maps-container ${isOpen ? 'open' : 'closed'} ${
        currentPano?.groupId === 'amenities' ? 'amenity' : 'default'
      }`}
    >
      {isDesktop ? (
        !isOpen ? (
          <button className="map-icon-button" onClick={handleToggleMap}>
            <MapIcon color="white" size={20} />
          </button>
        ) : !isExpanded ? (
          <button className="map-icon-button" onClick={handleToggleMap}>
            <CloseIcon color="white" size={28} />
          </button>
        ) : null
      ) : (
        <button className="map-icon-button" onClick={handleToggleMap}>
          {isOpen ? (
            <CloseIcon color="white" size={28} />
          ) : (
            <MapIcon color="white" size={20} />
          )}
        </button>
      )}

      {/* Floor Map Overlay - shown when open */}
      {isOpen && (
        <div
          className={`floor-maps-overlay ${isExpanded ? 'expanded' : ''}`}
          style={
            isExpanded && isDesktop
              ? { width: `${expandedWidth}px` }
              : undefined
          }
        >
          {/* Header with controls */}
          <div className="floor-maps-header">
            <div className="floor-maps-controls">
              {isDesktop && (
                <button
                  className={`expand-btn ${isExpanded ? 'expanded' : ''}`}
                  onClick={handleToggleExpand}
                >
                  <img
                    src={isExpanded ? quitFullscreenIcon : expandIcon}
                    alt={isExpanded ? 'Exit fullscreen' : 'Expand'}
                    className="expand-icon"
                  />
                </button>
              )}
              {/* {!isExpanded && (
                <button className="control-btn" onClick={handleToggleMap}>
                  <img src={cancelIcon} alt="Cancel" className="cancel-icon" />
                </button>
              )} */}
            </div>
          </div>

          {/* Floor plan image and hotspots */}
          <div className="floor-maps-content">
            <div className="floor-plan-wrapper">
              <img
                src={floorMap.image.url}
                alt={floorMap.image.name || 'Floor Plan'}
                className="floor-plan-image"
              />

              {/* Render hotspots */}
              {floorMap.objects &&
                floorMap.objects.map((hotspot, index) => {
                  const isActive = currentPano?._id === hotspot.id;
                  const isHovered = hoveredHotspot === hotspot.id;

                  return (
                    <>
                      <div
                        key={hotspot.id || index}
                        className={`floor-plan-hotspot ${
                          isActive ? 'active' : ''
                        } ${isHovered ? 'hovered' : ''}`}
                        style={{
                          left: `${hotspot.coordinates[0] * 100}%`,
                          top: `${hotspot.coordinates[1] * 100}%`,
                        }}
                        onClick={() => {
                          // console.log(
                          //   'Navigating to hotspot:',
                          //   hotspot.id,
                          //   mappedHotspot
                          // );
                          // if (mappedHotspot) {
                          //   history.push(mappedHotspot.url);
                          // }

                          handleHotspotClick(hotspot);
                          setHeadingIndicatorShow(false);
                        }}
                        onMouseEnter={() => setHoveredHotspot(hotspot.id)}
                        onMouseLeave={() => setHoveredHotspot(null)}
                      >
                        <div className="hotspot-dot" />
                        {isHovered && hotspot.title && (
                          <div className="hotspot-tooltip">{hotspot.title}</div>
                        )}
                      </div>

                      <div
                        className={`circular-sector ${
                          headingIndicatorShow ? 'show' : ''
                        }`}
                        style={{
                          position: 'absolute',
                          display: `${isActive ? 'block' : 'none'}`,
                          left: `${hotspot.coordinates[0] * 100}%`,
                          top: `${hotspot.coordinates[1] * 100}%`,
                        }}
                      >
                        <MinorSector
                          cameraParams={cameraParams}
                          sceneRotation={sceneRotation}
                          currentPano={currentPano}
                          defaultRotation={hotspot.defaultRotation}
                        />
                      </div>
                    </>
                  );
                })}
            </div>
          </div>
        </div>
      )}
    </div>,
    document.body
  );
};

export default FloorMaps;
